import { css, html } from "lit";

import { customElement, state } from "lit/decorators.js";

import { WowFormController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import "ui-components/button/wow-button.ts";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/wow-link/wow-link";
import "ui-components/wow-logo/wow-logo";
import { isValidPassword } from "helpers/password";
import { isValidRedirectUrl } from "helpers/url";

import { navigateTo } from "../router";
import { t, translate } from "../translations/i18n";
import { NotificationService } from "ui-components/notification";

const styles = css`
  :host {
    display: block;
    min-height: 100dvh;
    background-color: var(--sds-color-background-default-default);
    color: var(--sds-color-text-default-default);
    font-family: var(--sds-typography-family-body);
  }

  /* Base layout utilities */
  .container,
  .main-content,
  .header,
  .title-section,
  .form-section,
  .footer {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    max-width: 393px;
    margin: 0 auto;
    padding: var(--sds-size-space-800);
    min-height: 100dvh;
  }

  .logo {
    margin-bottom: var(--sds-size-space-1600);
  }

  .main-content {
    gap: var(--sds-size-space-800);
  }

  .header,
  .title-section {
    gap: var(--sds-size-space-800);
  }

  .title-section {
    gap: var(--sds-size-space-400);
  }

  .title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) / 1.2 var(--sds-typography-family-title);
  }

  .subtitle {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-03);
    line-height: 1.4;
    color: var(--sds-color-text-default-secondary);
  }

  .back-link {
    font-family: var(--sds-typography-family-body);
    font-weight: var(--sds-typography-weight-regular);
    font-size: var(--sds-typography-scale-02);
    line-height: 1.4;
    text-align: center;
    text-decoration: none;
  }

  .back-link:hover {
    text-decoration: underline;
  }

  .form-section {
    gap: var(--sds-size-space-600);
  }

  .submit-button {
    display: grid;
  }

  .footer {
    gap: var(--sds-size-space-1200);
    margin-top: var(--sds-size-space-800);
  }

  .form-error {
    color: var(--sds-color-text-danger-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  .form-success {
    color: var(--sds-color-text-success-default);
    font-size: var(--sds-typography-scale-02);
    margin-top: var(--sds-size-space-300);
    text-align: center;
  }

  form {
    display: contents;
  }
`;

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

@customElement("reset-password-page")
export class ResetPasswordPage extends SignalElement {
  static override styles = [styles];

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @state()
  private formError = "";

  @state()
  private formSuccess = "";

  @state()
  private isSubmitting = false;

  @state()
  private token = "";

  @state()
  private email = "";

  private returnTo: string | undefined;

  private get signInHref(): string {
    if (this.returnTo) {
      return this.returnTo.replace(/^\/my\//, "");
    }
    return "sign-in";
  }

  private formController: WowFormController<ResetPasswordFormData> = new WowFormController(
    this,
    {
      password: "",
      confirmPassword: "",
    },
    {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validators: {
        password: [
          (value: string) => {
            if (!value) return translate("reset-password.passwordRequired");

            const { minLength, maxLength, hasNumber, hasUpperLowerCase } = isValidPassword(value);

            if (!minLength) return translate("reset-password.passwordMinLength");
            if (!maxLength) return translate("reset-password.passwordMaxLength");
            if (!hasNumber) return translate("reset-password.passwordNumber");
            if (!hasUpperLowerCase) return translate("reset-password.passwordCase");

            return;
          },
        ],
        confirmPassword: [
          (value: string) => {
            const password = this.formController.getValue("password") as string;

            if (!value) {
              return translate("reset-password.passwordRequired");
            }
            // eslint-disable-next-line security/detect-possible-timing-attacks
            if (value !== password) {
              return translate("reset-password.passwordMismatch");
            }
            return;
          },
        ],
      },
    },
    async (event) => {
      this.formError = "";
      this.formSuccess = "";

      if (!event || !event.data || !event.isValid) {
        return;
      }

      const { data, isValid } = event;

      if (!isValid) {
        this.formError = translate("reset-password.fixErrors");
        return;
      }

      // Check if passwords match
      if (data.password !== data.confirmPassword) {
        this.formError = translate("reset-password.passwordMismatch");
        return;
      }

      this.isSubmitting = true;

      try {
        const result = await this.rpcService.call<{ success: boolean; message: string }>("reset-password", {
          token: this.token,
          email: this.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
        });

        if (result.success) {
          this.formSuccess = result.message || translate("reset-password.success");

          NotificationService.success(translate("reset-password.success"));

          if (this.returnTo) {
            window.location.href = this.returnTo;
          } else {
            await navigateTo("sign-in");
          }
        } else {
          this.formError = result.message || translate("reset-password.generalError");
        }
      } catch (error) {
        this.formError = translate("reset-password.generalError");
        console.error("Password reset error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  );

  override connectedCallback() {
    super.connectedCallback();
    this.extractUrlParams();
  }

  private extractUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    this.token = urlParams.get("token") || "";
    this.email = urlParams.get("email") || "";

    const returnTo = urlParams.get("returnTo");
    if (returnTo && isValidRedirectUrl(returnTo)) {
      this.returnTo = returnTo;
    }

    if (!this.token || !this.email) {
      this.formError = translate("reset-password.invalidLink");
    }
  }

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo></wow-logo>
        </div>

        <div class="main-content">
          <div class="header">
            <div class="title-section">
              <h1 class="title">${t("reset-password.title")}</h1>
              <p class="subtitle">${t("reset-password.subtitle")}</p>
            </div>
          </div>

          <form id="reset-password-form">
            <div class="form-section">
              <wow-input
                name=${this.formController.fieldNames.password}
                type="password"
                placeholder=${t("reset-password.newPassword")}
                autocomplete="new-password"
                required
                ?disabled=${this.isSubmitting}
              >
              </wow-input>

              <wow-input
                name=${this.formController.fieldNames.confirmPassword}
                type="password"
                placeholder=${t("reset-password.confirmPassword")}
                autocomplete="new-password"
                required
                ?disabled=${this.isSubmitting}
              >
              </wow-input>

              ${this.formError ? html`<div class="form-error">${this.formError}</div>` : ""}
              ${this.formSuccess ? html`<div class="form-success">${this.formSuccess}</div>` : ""}

              <wow-button
                form="reset-password-form"
                class="submit-button"
                type="submit"
                ?disabled=${this.isSubmitting || !this.token || !this.email}
              >
                ${this.isSubmitting ? t("reset-password.resetting") : t("reset-password.resetPassword")}
              </wow-button>
            </div>
          </form>
        </div>

        <div class="footer">
          <p>
            ${t("reset-password.backToSignIn")}
            <wow-link href=${this.signInHref}> ${t("reset-password.signIn")} </wow-link>
          </p>
        </div>
      </div>
    `;
  }
}
